import { useGetCdf2dDataFetcher } from 'features/dataLayers/load/useCdfRawValues2DArray'
import { getInspectionDate } from 'features/fde/inspectionDateHelpers'
import { runWebWorker } from 'features/webWorkerApi'
import { useCallback, useRef } from 'react'
import {
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoComponentDataFrame,
  GeckoInspection,
  GeckoMeasurementUnit,
  GeckoView,
} from 'types'
import { DATE_FORMAT } from 'utils/fde/constants'

import {
  createTminSource,
  serializeTminSource,
} from './calculations/calculateDerivedValues'
import { FdeCdfSpecification } from './fdeCdfSpecification'
import { getWorkerComponentConfig } from './getWorkerComponentConfig'
import {
  DerivedCdfWorkerCalculationParams,
  ParamOverrides,
} from './useGenerateDerivedCdfData'

type generateDerivedCdfDataFnProps = {
  cdfSpecification: FdeCdfSpecification
  componentId: string
  assetModel: GeckoAssetModel
  baseCDF: GeckoComponentDataFrame
  selectedInspection: GeckoInspection | undefined
  forecastComponentConfig: ForecastComponentConfig | undefined
  overrides?: ParamOverrides
  // if realInspectionDateForPredictionCalculation is defined, indicates that this is a predicted cdf
  // realInspectionDateForPredictionCalculation is the date of the most recent real inspection
  realInspectionDateForPredictionCalculation?: string
}

export function useGetGenerateDerivedCdfDataFn() {
  const getCdf2dDataFnPre = useGetCdf2dDataFetcher()

  /**
   * TODO [AQUA-276] - Remove this once the cache is a ref instead of a state
   *
   *
   * Storing the result of `useGetCdf2dDataFetcher` in a ref is a workaround to prevent infinite loops:
   *
   * When we generate a derived CDF we always put it in the "Rolodex Cache"; however, because the Rolodex-Cache storage variable
   * is a React-State, it gets a new reference on insert.  This update then re-initializes the `getCdf2dDataFnPre` callback.
   * If we were to use the result of `useGetCdf2dDataFetcher` directly in our returned callback, it would need to be included
   * in the dependency array (otherwise it wouldn't get the up-to-date cache values); however, that means that it will re-initialize
   * every time something is added to the cache.  Because the returned callback is almost always used to generate a value
   * for the Rolodex-Cache, this means that using it will cause an infinite loop
   *
   * (`get generate derived cdf data fn` -> `insert data into cache` -> `useGetCdf2dDataFetcher re-initializes`
   * -> `generate derived cdf data fn re-initializes` -> `insert new fn into cache` -> ... infinite loop ... )
   *
   *  By storing the return of `useGetCdf2dDataFetcher` in a ref, we wrap it in a stable reference -- with this, the "ref"'s reference (stable) can be injected
   *  into the callback, and through that reference var, the fn generated in this hook can access the latest value of the result of useGetGenerateDerivedCdfDataFn (reference
   *  varies) to always get up-to-date values.
   *
   */
  const stableRef = useRef<typeof getCdf2dDataFnPre>(getCdf2dDataFnPre)
  stableRef.current = getCdf2dDataFnPre

  return useCallback(
    async <RETURN = number>(
      props: generateDerivedCdfDataFnProps
    ): Promise<{
      calcParams: DerivedCdfWorkerCalculationParams
      array: RETURN[][]
    }> => {
      const cdfData =
        props.overrides?.thicknessValues2dArray ??
        (await stableRef.current(props.baseCDF))

      if (!cdfData) {
        throw new Error(
          'Failed to gather base-CDF data in derived cdf generation'
        )
      }

      const inspectionDate = getInspectionDate({
        inspectionSlug: props.selectedInspection?.id,
        inspection: props.selectedInspection,
      })

      const inspectionDateString = inspectionDate
        ? inspectionDate.format(DATE_FORMAT)
        : undefined

      const assetModelJson = props.assetModel.raw

      // Assemble the values needed for the remaining life calc
      // -- CDF data values
      const binWidthJson = props.baseCDF.data.binning.bin_size.width
        .convertTo(GeckoMeasurementUnit.Feet)
        .toJSON()
      const binHeightJson = props.baseCDF.data.binning.bin_size.height
        .convertTo(GeckoMeasurementUnit.Feet)
        .toJSON()
      const xGeometryStartJson = props.baseCDF.data.geometry.start.x
        .convertTo(GeckoMeasurementUnit.Feet)
        .toJSON()
      const yGeometryStartJson = props.baseCDF.data.geometry.start.y
        .convertTo(GeckoMeasurementUnit.Feet)
        .toJSON()

      const forecastComponentConfig = getWorkerComponentConfig(
        props.forecastComponentConfig
      )

      const calcParams: DerivedCdfWorkerCalculationParams = {
        componentId: props.componentId,
        thicknessValues: cdfData,
        thicknessUnit: props.baseCDF.data.params.measurement_unit.id,
        assetModelJson,
        inspectionDateString: inspectionDateString,
        realInspectionDateStringForPredictionCalculation:
          props.realInspectionDateForPredictionCalculation,
        binHeightJson,
        binWidthJson,
        yGeometryStartJson,
        xGeometryStartJson,
        forecastComponentConfig,
        tminSource: serializeTminSource(
          createTminSource({
            tminMethod: props.cdfSpecification.tminMethod,
            manualTminOverride: props.overrides?.manualTminOverride,
          })
        ),
        repairRegions: props.overrides?.appliedRegions,
        corrosionAugmentationRegions: props.overrides?.customCorrosionRegions,
        useCustomCR:
          props.cdfSpecification.view.id === GeckoView.CustomCRRemainingLife.id,
      }

      const workerRes = await runWebWorker<RETURN[][]>(
        props.cdfSpecification.getWorkerFile(),
        calcParams
      )

      return {
        calcParams: calcParams,
        array: workerRes,
      }
    },
    []
  )
}
